<template>
   <div class="flex flex-col gap-1" :class="{ 'w-full': grow }">
      <Label v-if="label" :disabled="disabled" :extra="extra" :physicalPermit="physicalPermit">
         {{ label }}
      </Label>
      <div class="text-primary relative">
         <input
            v-model="model"
            class="bg-light-grey rounded-small p-m placeholder-grey box-border h-10 px-4 outline-0 disabled:opacity-60"
            :type="type"
            :min="type === 'number' ? 0 : undefined"
            :class="{
               '!h-7 !rounded-sm !pl-2 disabled:opacity-100': physicalPermit,
               '__shadow bg-white': variant === 'completed',
               'bg-white': variant === 'white',
               'pl-12': iconBefore,
               'pr-12': iconAfter,
               'w-full': grow,
               'border-red border': error,
               'uppercase placeholder:normal-case': type === 'fiscal-code',
            }"
            :style="{
               'padding-right': `${
                  maxLengthRef
                     ? maxLengthRef.offsetWidth + 20
                     : slotRef
                       ? slotRef.offsetWidth + 20
                       : 0
               }px`,
            }"
            :maxlength="maxLength"
            :disabled="disabled"
            :placeholder="physicalPermit ? undefined : placeholder || label"
            @focus="emit('focus')"
            @blur="emit('blur')"
         />
         <Icon
            v-if="iconBefore"
            :name="iconBefore"
            class="absolute inset-y-0 left-4 size-6 translate-y-[35%] text-current"
         />

         <Icon
            v-if="iconAfter && !type === 'search'"
            :name="iconAfter"
            class="absolute inset-y-0 right-4 size-6 translate-y-[35%] text-current"
         />
         <Icon
            v-if="model && type === 'search'"
            name="close"
            class="text-grey __clickable starting:opacity-0 absolute inset-y-0 right-4 size-6 translate-y-[35%] transition-opacity duration-300"
            @click="model = ''"
         />

         <p
            v-if="maxLength"
            ref="maxLengthRef"
            class="text-grey absolute inset-y-0 right-4 flex items-center"
         >
            {{ model?.length || 0 }}/{{ maxLength }}
         </p>

         <div
            v-if="$slots.default"
            ref="slotRef"
            class="absolute inset-y-0 right-4 flex items-center"
         >
            <slot />
         </div>
      </div>
   </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

type Emit = {
   focus: []
   blur: []
}

export type TextInputType = 'email' | 'password' | 'text' | 'number' | 'search' | 'fiscal-code'

export type TextInputProps = {
   variant?: 'default' | 'white' | 'completed'
   label?: string
   placeholder?: string | number
   iconAfter?: string
   iconBefore?: string
   disabled?: boolean
   grow?: boolean
   type?: TextInputType
   error?: string
   maxLength?: number
   extra?: string
   physicalPermit?: boolean
}

const props = withDefaults(defineProps<TextInputProps>(), {
   variant: 'default',
   type: 'text',
})

const emit = defineEmits<Emit>()
const model = defineModel<string | number>()

const slotRef = ref<HTMLElement | null>(null)
const maxLengthRef = ref<HTMLElement | null>(null)
</script>

<style lang="postcss" scoped>
input[type='search']::-webkit-search-decoration {
   display: none;
}

input[type='search']::-webkit-search-cancel-button {
   display: none;
}

input[type='search']::-webkit-search-results-button {
   display: none;
}

input[type='search']::-webkit-search-results-decoration {
   display: none;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
   -webkit-appearance: none;
   margin: 0;
}

input[type='number'] {
   -moz-appearance: textfield;
}
</style>
